import React, { useState, useEffect } from 'react';
import { db } from '../firebase';
import { collection, getDocs } from 'firebase/firestore';
import { FaTimes, FaQuestionCircle, FaCalendarAlt, FaCopy, FaCheck } from 'react-icons/fa';
import { useNavigate } from 'react-router-dom';

const SurveyListPopup = ({ onClose, onSelectSurvey }) => {
  const [surveys, setSurveys] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [copiedId, setCopiedId] = useState(null);
  const navigate = useNavigate();

  const handleSurveySelect = (survey) => {
    onClose();
    navigate('/survey-builder', { state: { surveyToEdit: survey } });
  };

  const handleCopyId = async (e, id) => {
    e.stopPropagation();
    try {
      await navigator.clipboard.writeText(id);
      setCopiedId(id);
      setTimeout(() => setCopiedId((current) => (current === id ? null : current)), 1500);
    } catch (err) {
      console.error('Failed to copy survey ID:', err);
    }
  };

  useEffect(() => {
    const fetchSurveys = async () => {
      try {
        const surveysCollection = collection(db, 'EMA', 'data', 'surveys');
        const surveysSnapshot = await getDocs(surveysCollection);
        const surveysData = surveysSnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));
        setSurveys(surveysData);
      } catch (err) {
        console.error('Error fetching surveys:', err);
        setError('Failed to load surveys');
      } finally {
        setLoading(false);
      }
    };

    fetchSurveys();
  }, []);

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-3xl max-h-[85vh] flex flex-col relative">
        <button
          onClick={onClose}
          className="w-10 h-10 absolute top-2 right-2 text-gray-400 bg-gray-50 text-gray-400 hover:text-gray-500 hover:bg-gray-100 rounded-lg transition-colors"
        >
          <FaTimes className="h-5 w-5" />
        </button>
        <div className="p-6 border-b border-gray-200 flex justify-between items-center bg-gray-30 rounded-t-xl">
          <h2 className="text-2xl font-bold text-gray-900">Select Survey to Edit</h2>
        </div>

        <div className="p-6 overflow-y-auto flex-grow">
          {loading ? (
            <div className="flex items-center justify-center h-32">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
            </div>
          ) : error ? (
            <div className="text-center p-6 bg-red-50 rounded-lg">
              <p className="text-red-600 font-medium">{error}</p>
            </div>
          ) : surveys.length === 0 ? (
            <div className="text-center p-8 bg-gray-50 rounded-lg">
              <p className="text-gray-500 text-lg">No surveys found</p>
            </div>
          ) : (
            <div className="grid gap-4">
              {surveys.map((survey) => (
                <div
                  key={survey.id}
                  role="button"
                  tabIndex={0}
                  onClick={() => handleSurveySelect(survey)}
                  onKeyDown={(e) => {
                    if (e.target !== e.currentTarget) return;
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      handleSurveySelect(survey);
                    }
                  }}
                  className="w-full p-6 text-left cursor-pointer bg-white border border-gray-200 rounded-xl hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all shadow-sm hover:shadow-md"
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-grow">
                      <h3 className="text-xl font-semibold text-gray-900 mb-1">{survey.title}</h3>
                      <div className="flex items-center gap-2 mb-2">
                        <span className="text-xs text-gray-400 font-mono">ID: {survey.surveyId || survey.id}</span>
                        <button
                          type="button"
                          onClick={(e) => handleCopyId(e, survey.surveyId || survey.id)}
                          title="Copy survey ID"
                          aria-label="Copy survey ID"
                          className="shrink-0 w-auto p-1 text-gray-400 hover:text-blue-600 bg-transparent hover:bg-transparent rounded transition-colors"
                        >
                          {copiedId === (survey.surveyId || survey.id)
                            ? <FaCheck className="w-3.5 h-3.5 text-green-600" />
                            : <FaCopy className="w-3.5 h-3.5" />}
                        </button>
                      </div>
                      <p className="text-gray-600 mb-4 line-clamp-2">{survey.description}</p>
                      <div className="flex items-center space-x-4 text-sm text-gray-500">
                        <div className="flex items-center">
                          <FaQuestionCircle className="w-4 h-4 mr-1.5" />
                          <span>{survey.questions?.length || 0} questions</span>
                        </div>
                        
                          <div className="flex items-center">
                            <FaCalendarAlt className="w-4 h-4 mr-1.5" />
                            <span>Created: {new Date(survey.createdAt.seconds * 1000).toLocaleDateString()}</span>
                          </div>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default SurveyListPopup; 